/**
 * The Vuex module responsible for managing the validation state of the application.
 * This module tracks the validity of individual sections and steps, as well as the overall submission state.
 * It provides actions and mutations to update the validation state and notify the rest of the application.
 */

export default {
  namespaced: true,

  /**
   * The state object for the validation Vuex module, which tracks the validation
   * state of the application.
   *
   * @property {number} currentStep - The current step in the application.
   * @property {object} stepsValidity - An object mapping step numbers to their validity state.
   * @property {object} sectionsValidity - An object mapping section names to their validity state.
   * @property {object} sectionToStepMap - An object mapping section names to the step they belong to.
   * @property {object} shouldShowValidation - An object mapping section names to whether validation should be shown for them.
   * @property {boolean} userStepProgressAttempt - Whether the user has attempted to submit the form.
   * @property {boolean} isSubmitted - Whether the form has been successfully submitted.
   */
  state: {
    currentStep: 1,
    stepsValidity: {},
    sectionsValidity: {},
    sectionToStepMap: {},
    shouldShowValidation: {},
    userStepProgressAttempt: false,
    isSubmitted: false,
  },

  /**
   * Mutations for the validation Vuex module.
   *
   * @property {Function} INITIALIZE_SECTION_TO_STEP_MAP - Initializes the mapping of sections to their corresponding steps.
   * @property {Function} INITIALIZE_STEPS - Initializes the validity state for all steps and sections.
   * @property {Function} SET_SECTION_VALIDITY - Sets the validity state for a specific section.
   * @property {Function} SET_STEP_VALIDITY - Sets the validity state for a specific step.
   * @property {Function} SET_CURRENT_STEP - Sets the current step in the application.
   * @property {Function} SET_SHOULD_SHOW_VALIDATION - Sets whether validation should be shown for a specific section.
   * @property {Function} SET_HAS_ATTEMPTED_SUBMIT - Sets whether the user has attempted to submit the form.
   * @property {Function} SET_SUBMITTED - Sets whether the form has been successfully submitted.
   */
  mutations: {
    INITIALIZE_SECTION_TO_STEP_MAP(state, mapping) {
      state.sectionToStepMap = mapping;
    },
    INITIALIZE_STEPS(state, steps) {
      state.stepsValidity = steps.reduce((acc, step, index) => {
        acc[index + 1] = false;
        return acc;
      }, {});

      state.sectionToStepMap = steps.reduce((acc, step, stepIndex) => {
        step.sections.forEach((section) => {
          acc[section.name] = stepIndex + 1;
        });
        return acc;
      }, {});

      state.sectionsValidity = steps
        .flatMap((step) => step.sections)
        .reduce((acc, section) => {
          acc[section.name] = false;
          return acc;
        }, {});
    },
    SET_SECTION_VALIDITY(state, { section, isValid }) {
      state.sectionsValidity = {
        ...state.sectionsValidity,
        [section]: isValid,
      };
    },
    SET_STEP_VALIDITY(state, { step, isValid }) {
      state.stepsValidity = { ...state.stepsValidity, [step]: isValid };
    },
    SET_CURRENT_STEP(state, step) {
      state.currentStep = step;
    },
    SET_SHOULD_SHOW_VALIDATION(state, { section, value }) {
      state.shouldShowValidation = {
        ...state.shouldShowValidation,
        [section]: value,
      };
    },
    SET_HAS_ATTEMPTED_SUBMIT(state, value) {
      state.userStepProgressAttempt = value;
    },
    SET_SUBMITTED(state, value) {
      state.isSubmitted = value;
    },
  },
  actions: {
    initializeSteps({ commit }, steps) {
      commit("INITIALIZE_STEPS", steps);
    },
    initializeSectionToStepMap({ commit }, mapping) {
      commit("INITIALIZE_SECTION_TO_STEP_MAP", mapping);
    },
    updateCurrentStep({ commit }, step) {
      commit("SET_CURRENT_STEP", step);
    },
    updateSectionValidity({ commit, dispatch, state }, { section, isValid }) {
      commit("SET_SECTION_VALIDITY", { section, isValid });
      const step = state.sectionToStepMap[section];
      if (step) {
        dispatch("updateStepValidity", { step });
      }
    },
    updateStepValidity({ commit, state }, { step }) {
      const sectionsInStep = Object.entries(state.sectionToStepMap)
        .filter(([, stepId]) => stepId === step)
        .map(([section]) => section);
      const isStepValid = sectionsInStep.every(
        (section) => state.sectionsValidity[section]
      );
      commit("SET_STEP_VALIDITY", { step, isValid: isStepValid });
    },

    validateCurrentStep({ state, dispatch }) {
      const currentStepSections = Object.entries(state.sectionToStepMap)
        .filter(([, stepId]) => stepId === state.currentStep)
        .map(([section]) => section);

      currentStepSections.forEach((section) => {
        const isValid = state.sectionsValidity[section];
        dispatch("updateSectionValidity", { section, isValid });
      });
    },
    
    validatePlatformAuthorisation({ dispatch, commit, state, rootState }) {
      // Get all selected platforms
      const platforms = rootState.platforms.platforms;
      const selectedPlatforms = platforms.filter(p => p.selected);
      
      // If no platforms selected, section is invalid
      if (selectedPlatforms.length === 0) {
        dispatch("updateSectionValidity", {
          section: "PlatformAuthorisation",
          isValid: false
        });
        return;
      }

      // Check if all selected platforms are valid
      const platformValidationResults = selectedPlatforms.map(platform => {
        if (platform.id === 'aj-bell') {
          // AJ Bell requires credentials
          const hasCredentials = platform.credentials && platform.credentials.length > 0;
          const authConfirmed = platform.authorisation_confirmed;

          if (hasCredentials) {
            // Check if any credential set is incomplete (empty username or password)
            const hasIncompleteCredentials = platform.credentials.some(cred =>
              !cred.username || cred.username.trim() === '' ||
              !cred.password || cred.password.trim() === ''
            );

            // Use our own validation instead of relying on credentials_valid flag
            const actualCredentialsValid = !hasIncompleteCredentials;
            const isValid = actualCredentialsValid && authConfirmed;

            return isValid;
          }
          return false;
        }

        if (platform.id === 'transact') {
          // Transact credentials are optional BUT if provided, must have both username and password
          const authConfirmed = platform.authorisation_confirmed;

          if (!authConfirmed) {
            return false;
          }

          // Check credentials - must be either all empty or all filled (no partial entries)
          if (platform.credentials && platform.credentials.length > 0) {
            const allCredentialsValid = platform.credentials.every(cred => {
              const hasUsername = cred.username && cred.username.trim() !== '';
              const hasPassword = cred.password && cred.password.trim() !== '';

              // Both empty OR both filled = valid
              // Only one filled = invalid
              return (hasUsername && hasPassword) || (!hasUsername && !hasPassword);
            });

            return allCredentialsValid;
          }

          // No credentials at all is fine
          return true;
        }

        // For other platforms, just check authorization
        const isValid = platform.authorisation_confirmed;
        return isValid;
      });

      const allPlatformsValid = platformValidationResults.every(result => result === true);
      
      // Update section validity
      dispatch("updateSectionValidity", {
        section: "PlatformAuthorisation",
        isValid: allPlatformsValid
      });
      
      // If user has attempted to submit, show validation
      if (state.userStepProgressAttempt) {
        commit("SET_SHOULD_SHOW_VALIDATION", { 
          section: "PlatformAuthorisation", 
          value: true 
        });
      }
    },
    
    setShouldShowValidation({ commit }, { section, value }) {
      commit("SET_SHOULD_SHOW_VALIDATION", { section, value });
    },
    setUserStepProgressAttempt({ commit }, value) {
      commit("SET_HAS_ATTEMPTED_SUBMIT", value);
    },
    setSubmitted({ commit }, value) {
      commit("SET_SUBMITTED", value);
    },
    showThanksPage() {
      // Place holder to remove error as this has no validation
    },
  },
  getters: {
    isSectionValid: (state) => (section) =>
      state.sectionsValidity[section] || false,
    isStepValid: (state) => (step) => state.stepsValidity[step] || false,
    areAllStepsValid: (state) =>
      Object.values(state.stepsValidity).every((isValid) => isValid),
    getFirstInvalidSection: (state) => (step) => {
      const sectionsInStep = Object.entries(state.sectionToStepMap)
        .filter(([, stepId]) => stepId === step)
        .map(([section]) => section);
      return sectionsInStep.find((section) => !state.sectionsValidity[section]);
    },
    shouldShowSectionValidation: (state) => (section) =>
      state.shouldShowValidation?.[section] || false,
    userStepProgressAttempt: (state) => state.userStepProgressAttempt,
  },
};
